import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, Subscription } from "rxjs/Rx";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { debounceTime } from "rxjs/operator/debounceTime";
import { ICountry } from "../../../shared/location/countries-data";
import { FileUploadService } from "../../../shared/services/fileuploadservice";
import { LoaderService } from "../../../shared/services/loader.service";
import { CountryService } from "../../../shared/location/country.service";
import { AuthService } from "../../../shared/services/auth.service";
import { BackendService } from "../../../shared/services/backend.service";
import { AccountService } from "./../../../shared/account/account.service";
import * as _ from "lodash";
import * as firebase from "firebase/app";

@Component({
  selector: "app-country",
  templateUrl: "./country.component.html"
})
export class CountryComponent implements OnInit, OnDestroy {
  private _alert: Subject<string> = new Subject<string>();
  private alertSubscription: Subscription = new Subscription();
  modalReference: NgbModalRef;
  country: ICountry;
  countryMirrored: any;
  confirmationMessage = "";
  confirmationTitle = "";
  successMessage = "";
  errorMessage = "";
  nameNotUniqueError = "";
  name: string;
  query = "";
  actionType = "";
  fileName;
  fileSize;
  canUpload = false;
  file;
  fileUploadErrorMessage;
  sampleFileUrl = "https://bit.ly/2KYJD34";

  countriesCodes: Array<string>;
  defaultCountrycode = "NG";
  selectedCountryCode;
  nativeCountryName: string;
  commonCountryName: string;
  capital: string;
  continents: string;
  subRegion: string;
  callingCode: string;
  area: number;
  populationCount: number;
  populationPrecentage: number;
  languages: string;
  wikiLinkSuffix: string;
  anthemLink: string;
  countries = [];
  countryOptions = [];
  previousCountryCode: string;
  countryCodes = [];
  authState: firebase.User | null;
  userId: string;
  user: any;

  constructor(
    private authService: AuthService,
    private accountService: AccountService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
    private router: Router,
    private uploadService: FileUploadService,
    private countryService: CountryService
  ) {
    this.authState = this.authService.getAuthState();
    this.userId = BackendService.generateUserId(this.authState.email);
  }

  ngOnInit(): void {
    this.accountService.getProfile(this.userId).subscribe((profile: any) => {
      this.user = profile;
    });

    this.countryService.getCodesOfCountriesInUse().subscribe(codes => {
      this.countryCodes = codes;
    });

    this.selectedCountryCode = this.selectedCountryCode || this.defaultCountrycode;
    this.countriesCodes = this.countryService.getCountriesCodes();
    this.countryOptions = _.values(this.countryService.getFormattedCountries());
    this.onCountryCodeChanged(this.selectedCountryCode);
    this.countryService.getCountries().subscribe(countries => {
      this.countries = countries.map(country => {
        country["countryName"] = country["name"]["common"];

        return country;
      });
      this.countries.sort((a, b) => {
        const nameA = a.countryName.toUpperCase(); // ignore upper and lowercase
        const nameB = b.countryName.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });
    });
  }

  ngOnDestroy(): void {
    this.closeModal();
  }

  onCountryCodeChanged(countryCode): void {
    if (countryCode) {
      this.selectedCountryCode = countryCode;

      this.nativeCountryName = this.countryService.getCountryNativeName(this.selectedCountryCode);
    }
  }

  formatCountry(country): any {
    return country.toUpperCase();
  }

  showState(country): any {
    this.router.navigate([`basic/location/state/${this.formatCountry(country)}/r/0`]);
  }

  onChange(selectedCountryCode: string): void {
    this.selectedCountryCode = selectedCountryCode;
  }

  openFileUploader(): void {
    const element = document.getElementById("bulkUploader") as HTMLElement;
    element.click();
  }

  clearFileUpload(): void {
    this.fileName = "";
    this.fileSize = "";
    this.file = "";
    this.canUpload = false;
  }

  getFileUpload(event): void {
    const result = this.uploadService.verifyFileIsCSV(event);
    if (result.file) {
      this.fileName = result.file.name;
      this.fileSize = `${result.file.size}B`;
      this.file = result.file;
      this.canUpload = true;
    } else {
      this.fileUploadErrorMessage = result.message;
    }
  }

  upload(): void {
    this.closeModal();
  }

  createOrEditCountry(modal, country): void {
    if (country) {
      this.previousCountryCode = country.iso_3166_1_alpha2;
      this.country = country;
      this.name = country.name.common;
      this.selectedCountryCode = country.iso_3166_1_alpha2;
    } else {
      this.name = "";
      this.previousCountryCode = "";
    }
    this.openModal(modal, "md");
  }

  deleteCountry(): void {
    const status = this.countryService.delete(this.country);
    const message = status ? "Country successfully deleted" : "Oops!! Could not delete country, please try again later";
    this.closeModal();
    this.showAlert(status, message);
  }

  confirmAction(type, country: any, modal): void {
    this.country = country;
    this.actionType = type;
    const message = `Are you sure you want to ${type} ${country.name.common} ?`;
    this.confirm("Attention!", message, modal);
  }

  finishAction(): any {
    this.country = null;
  }

  saveCountry(): void {
    const status = this.countryService.save(this.selectedCountryCode, this.previousCountryCode);
    const message = status ? `Country successfully added` : `Oops!! Could not add country, please try again later`;
    this.showAlert(status, message);
    this.closeModal();
  }

  confirm(title, message, modal): void {
    this.confirmationTitle = title;
    this.confirmationMessage = message;
    this.openModal(modal, "sm");
  }

  openModal(modal, modalSize): void {
    this.modalReference = this.modalService.open(modal, { size: modalSize });
  }

  closeModal(): void {
    if (this.modalReference) {
      this.modalReference.close();
    }
  }

  showAlert(msgType, msg): void {
    this.alertSubscription = this._alert.subscribe(message =>
      msgType ? (this.successMessage = message) : (this.errorMessage = message)
    );
    debounceTime
      .call(this._alert, 3000)
      .subscribe(() => (msgType ? (this.successMessage = undefined) : (this.errorMessage = undefined)));
    this._alert.next(msg);
    this.alertSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/basic/location/country/country.component.ts